import type {
  DataObjectFieldTypes,
  DataObjectUISchemaFieldTypes
} from "@certa/types/src/dataObjects.types";
import type { IntlShape } from "react-intl";
import moment from "moment";

const DEFAULT_LOCALE = "en-US";
const DEFAULT_CURRENCY_SYMBOL = "USD";

const TRUTHY_VALUES = ["true", "yes", "1", 1, "True", "TRUE", true];
const FALSY_VALUES = ["false", "no", "0", 0, "False", "FALSE", false];

const getFormattedCurrency = (value: number, currencySymbol: string) => {
  return value.toLocaleString(DEFAULT_LOCALE, {
    style: "currency",
    currency: currencySymbol,
    currencyDisplay: "narrowSymbol"
  });
};

const getFormattedBoolean = (value: unknown, intl: IntlShape) => {
  const isString = typeof value === "string";
  const isNumber = typeof value === "number";
  const isBoolean = typeof value === "boolean";

  if (!isString && !isNumber && !isBoolean) {
    return "-";
  }
  const isTrue = TRUTHY_VALUES.includes(value);
  const isFalse = FALSY_VALUES.includes(value);
  if (isTrue) {
    return intl.formatMessage({
      id: "commonTextInstances.yes",
      defaultMessage: "Yes"
    });
  }
  if (isFalse) {
    return intl.formatMessage({
      id: "commonTextInstances.no",
      defaultMessage: "No"
    });
  }
  return "-";
};

type GetFormattedFieldValueParams = {
  value: string | number;
  type: DataObjectUISchemaFieldTypes | DataObjectFieldTypes;
  format?: string;
  intl: IntlShape;
};

export const getFormattedFieldValue = (
  params: GetFormattedFieldValueParams
) => {
  const { value, type, format, intl } = params;

  switch (type) {
    case "currency":
      const currencySymbol = format || DEFAULT_CURRENCY_SYMBOL;
      if (typeof value === "string") {
        return getFormattedCurrency(parseFloat(value), currencySymbol);
      }
      return getFormattedCurrency(value, currencySymbol);
    case "boolean":
      return getFormattedBoolean(value, intl);
    // TODO: Add formatting for other types
    case "array":
      if (Array.isArray(value)) {
        return value.filter(v => v !== "").join(", ");
      }
      return value;
    case "datetime":
      return moment(value).utc().format("MMM DD YYYY, hh:mm A");
    case "date":
      return moment.utc(value).format("MMM DD, YYYY");
    default:
      return value;
  }
};
